import { ReactElement, useCallback, useMemo } from 'react'
import { useFormContext } from 'react-hook-form'
import {
  calcRelativePositionOnWallAxis,
  calculatePointAlongWallAxisFromRelativePosition,
  projectPointOntoWallAxisCartesianAware,
} from '@geometry'
import { find } from 'lodash-es'
import { InputAdornment, Stack } from '@mui/material'
import { ImmutableVector3 } from '@modugen/ui-toolkit'
import {
  CoordinatePointInput,
  createReadOnlyPointManipulationConfig,
  RelativePositionField,
} from '@ui/forms'
import { useModelStore } from '@editorStores'

interface Props {
  guid: string
  length: number
  wallGuid?: string
  readOnly?: boolean
}

const FormFields = ({ guid, length, wallGuid, readOnly = false }: Props): ReactElement => {
  const rips = useModelStore(state => state.model.rips)
  const walls = useModelStore(state => state.model.walls)
  const rip = useMemo(() => find(rips, { position_guid: guid }) as Rip | undefined, [rips, guid])
  const wall = useMemo(() => {
    if (wallGuid) {
      return find(walls, { guid: wallGuid }) as ShapeObject | undefined
    }
    if (rip) {
      return find(walls, { guid: rip.wall_guid }) as ShapeObject | undefined
    }
    return undefined
  }, [wallGuid, rip, walls])
  const { setValue, getValues } = useFormContext()

  const handleRipStartReplace = useCallback(
    (x: number, y: number, z: number) => {
      if (!wall) return
      const { rip_start, rip_end } = getValues()
      if (!rip_start || !rip_end) return

      const ripStartPointFromReplacement = new ImmutableVector3(x, y, z)
      const ripStartPoint = new ImmutableVector3(
        ripStartPointFromReplacement.x,
        ripStartPointFromReplacement.y,
        rip_start.z,
      )

      const ripLength = rip_end.z - rip_start.z
      const newRipStartProjectedOntoWall = projectPointOntoWallAxisCartesianAware(
        wall,
        ripStartPoint,
      )
      const relativePositionFromStartNotClipped = calcRelativePositionOnWallAxis(
        wall,
        newRipStartProjectedOntoWall,
      )
      const relativePositionFromStart = Math.min(
        Math.max(relativePositionFromStartNotClipped, 0),
        1,
      )
      const newRipEndProjectedOntoWall = new ImmutableVector3(
        newRipStartProjectedOntoWall.x,
        newRipStartProjectedOntoWall.y,
        newRipStartProjectedOntoWall.z + ripLength,
      )
      const relativePositionFromStartAsString = String(relativePositionFromStart)

      setValue('relative_position', relativePositionFromStartAsString)
      setValue('rip_start', {
        x: newRipStartProjectedOntoWall.x,
        y: newRipStartProjectedOntoWall.y,
        z: newRipStartProjectedOntoWall.z,
      })
      setValue('rip_end', {
        x: newRipEndProjectedOntoWall.x,
        y: newRipEndProjectedOntoWall.y,
        z: newRipEndProjectedOntoWall.z,
      })
    },
    [wall, getValues, setValue],
  )

  const handleRipStartXChange = useCallback(
    (x: number | null) => {
      if (!x) return
      const { rip_start } = getValues()
      const y = rip_start.y
      const z = rip_start.z
      handleRipStartReplace(x, y, z)
    },
    [getValues, handleRipStartReplace],
  )

  const handleRipStartYChange = useCallback(
    (y: number | null) => {
      if (!y) return
      const { rip_start } = getValues()
      const x = rip_start.x
      const z = rip_start.z
      handleRipStartReplace(x, y, z)
    },
    [getValues, handleRipStartReplace],
  )

  const handleRelativePositionChange = useCallback(
    (relativePosition: string) => {
      if (!wall) return
      const { rip_start, rip_end } = getValues()
      if (!rip_start || !rip_end) return

      const start = calculatePointAlongWallAxisFromRelativePosition(wall, Number(relativePosition))
      const ripLength = rip_end.z - rip_start.z
      const end = new ImmutableVector3(start.x, start.y, start.z + ripLength)

      setValue('rip_start', { x: start.x, y: start.y, z: start.z })
      setValue('rip_end', { x: end.x, y: end.y, z: end.z })
    },
    [wall, getValues, setValue],
  )

  return (
    <>
      <Stack spacing={1}>
        {wall && (
          <>
            <CoordinatePointInput
              label="Start"
              name="rip_start"
              pointManipulation={
                readOnly ? createReadOnlyPointManipulationConfig() : { z: { disabled: true } }
              }
              onChangeX={readOnly ? undefined : handleRipStartXChange}
              onChangeY={readOnly ? undefined : handleRipStartYChange}
              onReplace={readOnly ? undefined : handleRipStartReplace}
            />
            <CoordinatePointInput
              label="Ende"
              name="rip_end"
              pointManipulation={createReadOnlyPointManipulationConfig()}
            />
          </>
        )}
        <RelativePositionField
          label="Pos. (vom Startpunkt d. Wand)"
          length={length}
          size="small"
          sx={{ width: '100%', bgcolor: 'grey.50', flexGrow: 1 }}
          name="relative_position"
          disabled={readOnly}
          onValueChange={readOnly ? undefined : handleRelativePositionChange}
          InputProps={{
            endAdornment: <InputAdornment position="end">m</InputAdornment>,
          }}
          data-cy="absolute-position-input"
        />
      </Stack>
    </>
  )
}

export default FormFields
